/**
 * Heurística PURA de progreso de visionado (F3-F). Alimenta el `watched_pct`
 * MONOTÓNICO de `video_watch_progress` (helper F2-B `upsertWatchProgress`) sin
 * que "saltar al final" cuente como visto.
 *
 * Modelo: se registra el conjunto de SEGUNDOS ENTEROS por los que ha pasado el
 * cursor DURANTE la reproducción (deltas de `timeupdate`). `watchedPct` =
 * segundos-cubiertos / duración. Saltar hacia adelante NO rellena los huecos;
 * re-ver un tramo no lo cuenta dos veces. `maxPosition` sigue el punto más
 * avanzado (para "reanudar donde quedó").
 *
 * Es un objeto de estado plano y determinista → testeable sin DOM.
 */

export interface WatchTrackerState {
  /** Segundos enteros cubiertos (índices 0..floor(duration)-1). */
  covered: Set<number>;
  /** Punto más avanzado alcanzado (segundos). Para reanudar. */
  maxPosition: number;
  /** Última posición observada (para calcular el delta del siguiente tick). */
  lastPosition: number;
  /** Duración total (segundos); 0 hasta conocerse (metadata). */
  duration: number;
}

/** Salto máximo (segundos) entre dos ticks que se considera reproducción
 * continua. Por encima es un SEEK: no se rellenan los segundos intermedios. */
const CONTINUOUS_TICK_MAX = 2;

export function createWatchTrackerState(duration = 0, initialPct = 0): WatchTrackerState {
  const state: WatchTrackerState = {
    covered: new Set<number>(),
    maxPosition: 0,
    lastPosition: 0,
    duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
  };
  // Siembra el progreso previo (monotónico): marca los primeros N segundos como
  // cubiertos para que un `watched_pct` sincronizado no retroceda al reabrir.
  if (initialPct > 0 && state.duration > 0) {
    const seeded = Math.round(clamp01(initialPct) * Math.ceil(state.duration));
    for (let i = 0; i < seeded; i++) {
      state.covered.add(i);
    }
  }
  return state;
}

/** Fija/actualiza la duración (al conocerse por metadata). */
export function setTrackerDuration(state: WatchTrackerState, duration: number): void {
  if (Number.isFinite(duration) && duration > 0) {
    state.duration = duration;
  }
}

/**
 * Registra una posición de reproducción (llamado en cada `timeupdate`). Si el
 * salto respecto a la última posición es continuo (≤ CONTINUOUS_TICK_MAX) marca
 * los segundos intermedios como cubiertos; si es un seek, solo actualiza la
 * referencia sin rellenar.
 */
export function trackPosition(state: WatchTrackerState, position: number): void {
  if (!Number.isFinite(position) || position < 0) {
    return;
  }
  const prev = state.lastPosition;
  const delta = position - prev;
  if (delta > 0 && delta <= CONTINUOUS_TICK_MAX) {
    const from = Math.floor(prev);
    const to = Math.floor(position);
    for (let s = from; s <= to; s++) {
      state.covered.add(s);
    }
  } else if (delta >= 0 && delta <= CONTINUOUS_TICK_MAX) {
    // Mismo segundo / avance ínfimo: marca el segundo actual.
    state.covered.add(Math.floor(position));
  }
  // Seek (delta grande o negativo): no rellena; solo referencia nueva.
  state.lastPosition = position;
  if (position > state.maxPosition) {
    state.maxPosition = position;
  }
}

/** Fracción vista [0,1] según la cobertura de segundos. */
export function watchedFraction(state: WatchTrackerState): number {
  if (state.duration <= 0) {
    return 0;
  }
  const total = Math.ceil(state.duration);
  if (total <= 0) {
    return 0;
  }
  return clamp01(state.covered.size / total);
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.min(1, Math.max(0, n));
}
